import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import pb from '@/lib/pb'
import { COLLECTIONS } from '@/types'
import type { TTenant } from '@/types'
import { usePbSubscription } from '@/lib/usePbSubscription'

export function useTenants() {
  usePbSubscription(COLLECTIONS.TENANTS, ['tenants'])
  return useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const records = await pb.collection(COLLECTIONS.TENANTS).getFullList<TTenant>({
        filter: 'is_deleted != true',
      })
      return records.sort((a, b) => (b.id > a.id ? 1 : b.id < a.id ? -1 : 0))
    },
  })
}

export function useTenant(id: string) {
  usePbSubscription(COLLECTIONS.TENANTS, ['tenants', id])
  return useQuery({
    queryKey: ['tenants', id],
    queryFn: async () => {
      const record = await pb.collection(COLLECTIONS.TENANTS).getOne<TTenant>(id)
      return record
    },
    enabled: !!id,
  })
}

export function useCreateTenant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<TTenant>) => {
      return pb.collection(COLLECTIONS.TENANTS).create<TTenant>(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    },
  })
}

export function useUpdateTenant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TTenant> }) => {
      return pb.collection(COLLECTIONS.TENANTS).update<TTenant>(id, data)
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      queryClient.invalidateQueries({ queryKey: ['tenants', id] })
    },
  })
}

export function useDeleteTenant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      return pb.collection(COLLECTIONS.TENANTS).update(id, {
        is_deleted: true,
        deleted_at: new Date().toISOString(),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    },
  })
}
